import { Stack, Typography } from '@mui/material';
import { ProductGroups, Products, Routes } from 'enums';
import { useHasLicense } from 'hooks/use-has-license';
import CustomerOnboarding from 'features/onboarding/general-onboarding-journey/customer-onboarding';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import { makeStyles } from 'tss-react/mui';
import FeatureItemAdvanced from '../features/feature-overview/feature-item-advanced';
import ProductGroup from '../features/feature-overview/product-group';

const FeatureOverview: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const setAppBarNavigation = useAppBarNavigationStore((state) => state.setActiveTab);

  const hasRecruiterCoPilot = useHasLicense(Products.SAIA_RECRUITING);
  const hasLeadGenCoPilot = useHasLicense(Products.SAIA_SALES);

  const goUpgrade = (product: string) => () => navigate(`${Routes.UPGRADE}?product=${product}`);

  const handleNavigation = (route: Routes, activeTab: NavigationStates = NavigationStates.TOOL) => {
    return () => {
      navigate(route);
      setAppBarNavigation(activeTab);
    };
  };

  const groups = [
    {
      key: 'hr',
      hasLicense: hasRecruiterCoPilot,
      node: (
        <ProductGroup key="hr" productGroup={ProductGroups.HR_BOOSTER} productGroupName={t('hrBoosterActiveSourcing')}>
          <FeatureItemAdvanced
            productGroup={ProductGroups.HR_BOOSTER}
            featureName={t('hrBoosterAutoCampaignProduct')}
            descriptionPrimary={t('hrBoosterAutoCampaignProductDescriptionMain')}
            descriptionSecondary={t('hrBoosterAutoCampaignProductDescriptionSub')}
            features={[t('fullyAutomated'), t('aiTextGeneration'), t('matchPersonalityProfile')]}
            action={handleNavigation(Routes.RECRUITING_BOOSTER_CO_PILOT_PROJECTS, NavigationStates.PROJECTS)}
            hot={true}
            disabled={!hasRecruiterCoPilot}
            onBuy={!hasRecruiterCoPilot ? goUpgrade('hr-booster') : undefined}
          />
        </ProductGroup>
      ),
    },
    {
      key: 'lead',
      hasLicense: hasLeadGenCoPilot,
      node: (
        <ProductGroup key="lead" productGroup={ProductGroups.LEAD_BOOSTER} productGroupName={t('leadBoosterSocialSelling')}>
          <FeatureItemAdvanced
            productGroup={ProductGroups.LEAD_BOOSTER}
            featureName={t('leadBoosterAutoCampaignProduct')}
            descriptionPrimary={t('leadBoosterAutoCampaignProductDescriptionMain')}
            descriptionSecondary={t('leadBoosterAutoCampaignProductDescriptionSub')}
            features={[t('fullyAutomated'), t('aiTextGeneration'), t('matchPersonalityProfile')]}
            action={handleNavigation(Routes.LEAD_BOOSTER_CO_PILOT_PROJECTS, NavigationStates.PROJECTS)}
            hot={true}
            disabled={!hasLeadGenCoPilot}
            onBuy={!hasLeadGenCoPilot ? goUpgrade('lead-booster') : undefined}
          />
        </ProductGroup>
      ),
    },
  ];

  const orderedGroups = groups.sort((a, b) => (a.hasLicense === b.hasLicense ? 0 : a.hasLicense ? -1 : 1));

  const activeLicenses = groups.filter((g) => g.hasLicense).length;
  const lockedProductNames = [
    !hasRecruiterCoPilot ? t('saiaRecruiting') : null,
    !hasLeadGenCoPilot ? t('saiaSales') : null,
  ].filter((name): name is string => Boolean(name));

  const subtitle = [
    t('featureOverviewLicenseSummary', { active: activeLicenses, total: groups.length }),
    lockedProductNames.length ? t('featureOverviewLicenseLocked', { products: lockedProductNames.join(', ') }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <CustomerOnboarding />
      <Stack className={classes.featureOverview} gap={5}>
        <Stack className={classes.pageHeader}>
          <Typography className={classes.pageTitle}>{t('products')}</Typography>
          <Typography className={classes.pageSubtitle}>{subtitle}</Typography>
        </Stack>
        {orderedGroups.map((g) => g.node)}
      </Stack>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  featureOverview: {
    background: theme.palette.backgrounds.main_new,
    minHeight: '100%',
    padding: '20px 16px 16px 48px',
    [theme.breakpoints.down('lg')]: {
      padding: '20px 8px 8px 8px',
    },
  },
  pageHeader: {
    gap: '4px',
  },
  pageTitle: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 500,
    letterSpacing: '-0.2px',
    color: theme.palette.text.primary,
  },
  pageSubtitle: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
}));

export default FeatureOverview;
